import JSZip from "jszip";
import type { BuildContext } from "@/features/exports/services/DownloadManagerContext";
import { supabase } from "@/shared/integrations/supabase/client";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import { sanitize } from "@/features/exports/services/_helpers";
import { fetchBytes } from "./shared";

import { buildAssetRegisterXlsx } from "./buildDeficienciesPortfolioBundle/buildAssetRegisterXlsx";
import { buildCategoryNotesXlsx } from "./buildDeficienciesPortfolioBundle/buildCategoryNotesXlsx";
import { buildSurveyAttributesXlsx } from "./buildDeficienciesPortfolioBundle/buildSurveyAttributesXlsx";
import {
  fetchAssetNotes,
  fetchAssets,
  fetchLatestAttributes,
  fetchLatestSiteSummaryDocuments,
  fetchPropertyIdsWithDeficiencies,
} from "./buildDeficienciesPortfolioBundle/fetchers";
import { propertyLabel, runWithConcurrency } from "./buildDeficienciesPortfolioBundle/helpers";
import {
  REPORT_DOWNLOAD_CONCURRENCY,
  type Args,
  type PropertyRow,
} from "./buildDeficienciesPortfolioBundle/types";

export async function buildDeficienciesPortfolioBundle(
  { projectId, propertyId }: Args,
  ctx: BuildContext,
): Promise<Blob> {
  ctx.setProgress(2, "Loading portfolio…");

  const [{ data: project }, authorizedProperties] = await Promise.all([
    supabase.from("projects").select("id, name").eq("id", projectId).maybeSingle(),
    fetchClientProperties<PropertyRow>(
      projectId,
      "id, name, address, city, state, store_number",
      (q) => (propertyId ? q.eq("id", propertyId) : q),
    ),
  ]);

  // store_number ascending, nulls last (matches the prior DB ordering) for the
  // full-portfolio case; single-property case needs no sort.
  const propertyList = propertyId
    ? authorizedProperties
    : authorizedProperties.sort((a, b) => {
        if (a.store_number == null && b.store_number == null) return 0;
        if (a.store_number == null) return 1;
        if (b.store_number == null) return -1;
        return String(a.store_number).localeCompare(String(b.store_number), undefined, {
          numeric: true,
        });
      });
  if (propertyList.length === 0) {
    throw new Error(propertyId ? "Property not found" : "No properties found for portfolio bundle");
  }

  const projectLabel = project?.name ?? "Portfolio";
  const rootName = sanitize(
    propertyId && propertyList[0]
      ? `${propertyList[0].name || "Property"}_deficiencies_package`
      : `${projectLabel}_deficiencies_package`
  );
  const zip = new JSZip();
  const root = zip.folder(rootName)!;
  const propertyMap = new Map(propertyList.map((property) => [property.id, property]));
  const deficiencyPropertyIds = await fetchPropertyIdsWithDeficiencies(
    projectId,
    propertyList.map((property) => property.id),
  );
  const reportProperties = propertyList.filter((property) => deficiencyPropertyIds.has(property.id));

  const assetRegisterName = propertyId ? "Asset Register.xlsx" : "Portfolio Asset Register.xlsx";
  const surveyAttributesName = propertyId ? "Survey Attributes.xlsx" : "Portfolio Survey Attributes.xlsx";
  const categoryNotesName = propertyId ? "Category Notes.xlsx" : "Portfolio Category Notes.xlsx";

  root.file(
    "INFO.txt",
    [
      propertyId && propertyList[0]
        ? `${propertyList[0].name || "Property"} — Deficiencies Export Package`
        : `${projectLabel} — Deficiencies Export Package`,
      `Generated: ${new Date().toISOString()}`,
      `Properties: ${propertyList.length}`,
      `Per-site summary reports: ${reportProperties.length}`,
      "",
      "Contents:",
      `  ${assetRegisterName}`,
      `  ${surveyAttributesName}`,
      `  ${categoryNotesName}`,
      "  Per Site Summary Report/*.pdf",
    ].join("\n"),
  );

  ctx.setProgress(10, "Building asset register…");
  const assets = await fetchAssets(propertyList.map((property) => property.id));
  root.file(assetRegisterName, buildAssetRegisterXlsx(assets, propertyMap));

  ctx.setProgress(32, "Building survey attributes…");
  const attributes = await fetchLatestAttributes(propertyList.map((property) => property.id));
  root.file(surveyAttributesName, buildSurveyAttributesXlsx(attributes, propertyList));

  ctx.setProgress(48, "Building category notes…");
  const assetNotes = await fetchAssetNotes(assets.map((asset) => asset.id));
  root.file(categoryNotesName, buildCategoryNotesXlsx(assets, assetNotes, propertyMap));

  ctx.setProgress(58, "Generating per-site PDFs…");
  const reportsFolder = root.folder("Per Site Summary Report")!;
  const failures: string[] = [];
  const siteSummaryDocuments = await fetchLatestSiteSummaryDocuments(
    reportProperties.map((property) => property.id),
  );
  let completedReports = 0;

  await runWithConcurrency(
    reportProperties.map((property) => async () => {
      if (ctx.signal.aborted) throw new Error("Bundle generation cancelled");

      const label = propertyLabel(property);
      const document = siteSummaryDocuments.get(property.id);

      try {
        if (!document?.file_path) {
          throw new Error("No site_summary document found");
        }

        const pdf = await fetchBytes(document.file_path, { signal: ctx.signal, timeoutMs: 60_000 });
        if (!pdf) {
          throw new Error("Unable to download site_summary document");
        }

        reportsFolder.file(`${sanitize(label)}.pdf`, pdf);
      } catch (err) {
        failures.push(`${label}: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        completedReports += 1;
        ctx.setProgress(
          58 + Math.round((completedReports / reportProperties.length) * 34),
          `Downloaded PDF ${completedReports} / ${reportProperties.length}`,
        );
      }
    }),
    REPORT_DOWNLOAD_CONCURRENCY,
  );

  if (reportProperties.length === 0) {
    reportsFolder.file("NO_REPORTS.txt", "No properties with deficiencies were found.");
  }

  if (failures.length > 0) {
    reportsFolder.file("FAILED_REPORTS.txt", failures.join("\n"));
  }

  ctx.setProgress(96, "Compressing…");
  const blob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
  ctx.setProgress(100, "Done");
  return blob;
}
